import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, Radio } from "lucide-react";
import { routeInfo } from "@/lib/maps";

const TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION = "&copy; OpenStreetMap";
const REROUTE_METERS = 350;

const carIcon = L.divIcon({
  className: "live-car",
  html: `<div style="width:34px;height:34px;border-radius:50%;background:#d4af37;
    box-shadow:0 0 0 6px rgba(212,175,55,.22),0 4px 12px rgba(0,0,0,.5);display:flex;
    align-items:center;justify-content:center">
    <svg viewBox="0 0 24 24" width="18" height="18" fill="#0f131c"><path d="M5 11l1.5-4.5A2 2 0
      0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11v6h-2.2a2.3 2.3 0 0 1-4.6 0h-0.4a2.3 2.3 0 0
      1-4.6 0H5v-6zm2.2 0h9.6l-1-3H8.2l-1 3z"/></svg></div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const targetIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#e5e7eb;border:3px solid
    #0f131c;box-shadow:0 2px 8px rgba(0,0,0,.5)"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const metresBetween = (a, b) => {
  if (!a || !b) return null;
  const r = 6371000;
  const p1 = (a.lat * Math.PI) / 180;
  const p2 = (b.lat * Math.PI) / 180;
  const dp = p2 - p1;
  const dl = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/** The chauffeur's car, live, with the driving line to wherever they are headed. */
export const LiveTripMap = ({ driver, target, label = "Pickup", testId = "live-map",
                             heightClass = "h-72" }) => {
  const holder = useRef(null);
  const map = useRef(null);
  const car = useRef(null);
  const pin = useRef(null);
  const line = useRef(null);
  const routedFrom = useRef(null);
  const [routing, setRouting] = useState(false);

  useEffect(() => {
    if (!holder.current || map.current) return;
    map.current = L.map(holder.current, { zoomControl: true, attributionControl: true })
      .setView([driver?.lat ?? 29.76, driver?.lng ?? -95.37], 12);
    L.tileLayer(TILES, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(map.current);
    return () => { map.current?.remove(); map.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The car moves on every ping; CSS slides it so it never teleports.
  useEffect(() => {
    if (!map.current || !driver?.lat) return;
    const at = [driver.lat, driver.lng];
    if (car.current) {
      car.current.setLatLng(at);
    } else {
      car.current = L.marker(at, { icon: carIcon, zIndexOffset: 600 }).addTo(map.current);
    }
    if (!map.current.getBounds().pad(-0.15).contains(at)) map.current.panTo(at);
  }, [driver?.lat, driver?.lng]);

  useEffect(() => {
    if (!map.current || !target?.lat) return;
    const at = [target.lat, target.lng];
    if (pin.current) pin.current.setLatLng(at);
    else pin.current = L.marker(at, { icon: targetIcon }).addTo(map.current).bindTooltip(label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.lat, target?.lng]);

  // Redraw the driving line only when the car has really moved — the route API is rate limited.
  useEffect(() => {
    if (!map.current || !driver?.lat || !target?.lat) return;
    const moved = metresBetween(routedFrom.current, driver);
    if (line.current && moved !== null && moved < REROUTE_METERS) return;
    let cancelled = false;
    setRouting(true);
    (async () => {
      const info = await routeInfo([driver, target]);
      if (cancelled || !map.current) return;
      const path = info?.path?.length ? info.path : [[driver.lat, driver.lng], [target.lat, target.lng]];
      if (line.current) map.current.removeLayer(line.current);
      line.current = L.polyline(path, { color: "#d4af37", weight: 4, opacity: 0.9 }).addTo(map.current);
      if (!routedFrom.current) {
        map.current.fitBounds(L.latLngBounds(path), { padding: [30, 30], maxZoom: 15 });
      }
      routedFrom.current = { lat: driver.lat, lng: driver.lng };
      setRouting(false);
    })();
    return () => { cancelled = true; };
  }, [driver?.lat, driver?.lng, target?.lat, target?.lng]);

  return (
    <div data-testid={testId} className="flex h-full flex-col overflow-hidden rounded-xl border border-gold/25">
      <div ref={holder} className={`w-full bg-ink-900 ${heightClass === "h-full" ? "min-h-0 flex-1" : heightClass}`}
        data-testid={`${testId}-canvas`} />
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-ink-600 bg-ink-900/80 px-4 py-2.5">
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
          <Radio className="h-3 w-3 animate-pulse" /> Live · following your car
        </p>
        {routing
          ? <Loader2 data-testid={`${testId}-routing`} className="h-3.5 w-3.5 animate-spin text-slate-500" />
          : <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">to {label}</span>}
      </div>
    </div>
  );
};
